import React from 'react';
import { StarIcon, ThumbsUpIcon, HeartIcon, GlobeIcon } from 'lucide-react';
interface ReviewProps {
  initials: string;
  name: string;
  color: string;
  time: string;
  text: string;
  likes: number;
}
function ReviewCard({
  initials,
  name,
  color,
  time,
  text,
  likes
}: ReviewProps) {
  return <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-5 md:p-6" data-id="element-234">
      {/* Header */}
      <div className="flex items-center gap-3 mb-3" data-id="element-235">
        <div className={`w-12 h-12 rounded-full flex items-center justify-center font-bold text-white text-sm ${color}`} data-id="element-236">
          {initials}
        </div>
        <div data-id="element-237">
          <p className="font-bold text-brown-dark text-base" data-id="element-238">{name}</p>
          <div className="flex items-center gap-1.5 text-xs text-gray-400" data-id="element-239">
            <span data-id="element-240">{time}</span>
            <span data-id="element-241">·</span>
            <GlobeIcon className="w-3 h-3" data-id="element-242" />
          </div>
        </div>
      </div>

      {/* Stars */}
      <div className="flex gap-0.5 mb-3" data-id="element-243">
        {[...Array(5)].map((_, i) => <StarIcon key={i} className="w-5 h-5 fill-caramel text-caramel" data-id="element-244" />)}
      </div>

      {/* Review Text */}
      <p className="text-gray-700 text-[15px] leading-relaxed mb-4" data-id="element-245">{text}</p>

      {/* Reactions */}
      <div className="flex items-center justify-between pt-3 border-t border-gray-100 text-sm text-gray-500" data-id="element-246">
        <div className="flex items-center gap-1.5" data-id="element-247">
          <span className="flex -space-x-1" data-id="element-248">
            <span className="w-5 h-5 bg-blue-500 rounded-full flex items-center justify-center" data-id="element-249">
              <ThumbsUpIcon className="w-3 h-3 text-white" data-id="element-250" />
            </span>
            <span className="w-5 h-5 bg-red-400 rounded-full flex items-center justify-center" data-id="element-251">
              <HeartIcon className="w-3 h-3 text-white" data-id="element-252" />
            </span>
          </span>
          <span className="font-medium" data-id="element-253">{likes}</span>
        </div>
        <div className="flex items-center gap-4 font-semibold" data-id="element-254">
          <span className="hover:underline cursor-pointer" data-id="element-255">Me gusta</span>
          <span data-id="element-256">·</span>
          <span className="hover:underline cursor-pointer" data-id="element-257">Responder</span>
          <span data-id="element-258">·</span>
          <span data-id="element-259">{time}</span>
        </div>
      </div>
    </div>;
}
export function StudentReviews() {
  const reviews: ReviewProps[] = [{
    initials: 'MR',
    name: 'María Rosa Gutiérrez',
    color: 'bg-[#8B6F47]',
    time: '1 d',
    text: 'Yo no tenía tiempo y me daba miedo que salga mal y tener que tirar los ingredientes. Empesé con una tanda chica, y me sorprendió lo rápido que sale. Ya llevo 3 semanas vendiendo y recuperé la inversión el primer día. ¡Súper recomendado!',
    likes: 252
  }, {
    initials: 'ME',
    name: 'María Elena Contreras',
    color: 'bg-emerald-700',
    time: '2 d',
    text: 'Recibí este material hace unos días y no he podido dejarlo. Hay explicaciones claras con contexto completo de cada receta. ¡Resultó ser aún mejor de lo que esperaba! Las explicaciones son súper fáciles de leer y comprender, a diferencia de otros materiales que encontré por internet.',
    likes: 189
  }, {
    initials: 'CL',
    name: 'Carmen López',
    color: 'bg-pink-600',
    time: '3 d',
    text: 'Antes probaba recetas sueltas y era frustrante. Acá sentí que por fin tengo un método: sabores, salsas y cómo venderlo sin vergüenza. Mis flanes ahora salen perfectos y ya tengo clientas fijas que me piden cada semana.',
    likes: 174
  }, {
    initials: 'AP',
    name: 'Ana Patricia Mendoza',
    color: 'bg-violet-600',
    time: '4 d',
    text: 'Compré por la garantía. Me llegó al instante al correo. Lo vi, lo entendí y dije: "ok, esto sí es para mí". Las salsas son un plus increíble, mis clientes pagan más por el flan con salsa. ¡Mejor inversión que he hecho!',
    likes: 203
  }, {
    initials: 'LF',
    name: 'Lucía Fernández',
    color: 'bg-amber-700',
    time: '5 d',
    text: 'Soy mamá de 3 y no tenía tiempo para nada. Con este método hago 10 flanes mientras los niños duermen y los vendo al día siguiente. Ya generé más de $500 en el primer mes. Las guías de WhatsApp me ayudaron muchísimo a perder la vergüenza de ofrecer.',
    likes: 231
  }];
  return <section className="py-16 md:py-20 px-4 bg-cream-dark border-t border-caramel/20" data-id="element-260">
      <div className="max-w-2xl mx-auto" data-id="element-261">
        <h2 className="text-3xl md:text-4xl font-heading font-bold text-center text-brown-dark mb-6" data-id="element-262">
          Lo que dicen nuestras estudiantes
        </h2>

        {/* Rating Summary */}
        <div className="flex flex-col items-center mb-10" data-id="element-263">
          <div className="flex items-center gap-2 mb-2" data-id="element-264">
            <span className="text-5xl md:text-6xl font-heading font-extrabold text-brown-dark" data-id="element-265">
              4.9
            </span>
            <div className="flex flex-col" data-id="element-266">
              <div className="flex gap-0.5" data-id="element-267">
                {[...Array(5)].map((_, i) => <StarIcon key={i} className="w-6 h-6 md:w-7 md:h-7 fill-caramel text-caramel" data-id="element-268" />)}
              </div>
              <span className="text-sm text-gray-500 font-medium mt-0.5" data-id="element-269">
                Basado en +2037 reseñas
              </span>
            </div>
          </div>
        </div>

        {/* Review Cards */}
        <div className="space-y-4" data-id="element-270">
          {reviews.map((review, i) => <ReviewCard key={i} {...review} data-id="element-271" />)}
        </div>
      </div>
    </section>;
}